import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import './camera.scss';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import { getCameraStream } from '../actions';
import notificationDecorator, { errorNotification } from '../../Notifications/notificationDecorator';
import { DOMAIN_NAME, resolveMediaServerUrl } from '../../../configs';
import { getResponseError } from '../../../utils/utils';

const configuration = {
  iceServers: [{
    urls: [
      `turn:${DOMAIN_NAME}:15067?transport=udp`,
      `turn:${DOMAIN_NAME}:15067?transport=tcp`,
    ],
    username: 'demo',
    credential: 'demo',
  }],
  sdpSemantics: 'unified-plan',
};
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 5000;
const VIDEO_STALL_TIMEOUT_MS = 15000;
const FRAME_WATCHDOG_INTERVAL_MS = 5000;
const ICE_GATHERING_TIMEOUT_MS = 1000;

function waitForIceCandidates(peerConnection) {
  if (peerConnection.iceGatheringState === 'complete') {
    return Promise.resolve();
  }

  return new Promise(resolve => {
    let timeout;
    const finish = () => {
      clearTimeout(timeout);
      peerConnection.removeEventListener('icegatheringstatechange', handleStateChange);
      resolve();
    };
    const handleStateChange = () => {
      if (peerConnection.iceGatheringState === 'complete') {
        finish();
      }
    };

    peerConnection.addEventListener('icegatheringstatechange', handleStateChange);
    timeout = setTimeout(() => {
      window.console.warn(
        `ICE gathering did not complete within ${ICE_GATHERING_TIMEOUT_MS} ms; sending available candidates.`,
      );
      finish();
    }, ICE_GATHERING_TIMEOUT_MS);
  });
}

export default function CameraView(props) {
  const { selectedCamera = null, noCameraText = '' } = props;
  const dispatch = useDispatch();
  const [isPlay, setIsPlay] = useState(false);
  const [noSignal, setNoSignal] = useState(false);
  const videoRef = useRef(null);
  const connectionRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const stallTimerRef = useRef(null);
  const frameWatchdogRef = useRef(null);
  const frameCallbackRef = useRef(null);
  const streamUrlRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const lastFrameAtRef = useRef(0);
  const lastFrameCountRef = useRef(0);
  const userPausedRef = useRef(false);
  const stoppedRef = useRef(false);
  const intl = useIntl();
  const { formatMessage } = intl;
  const id = selectedCamera && selectedCamera.id ? selectedCamera.id : null;

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const clearStallTimer = () => {
    if (stallTimerRef.current) {
      clearTimeout(stallTimerRef.current);
      stallTimerRef.current = null;
    }
  };

  const clearFrameWatchdog = () => {
    if (frameWatchdogRef.current) {
      clearInterval(frameWatchdogRef.current);
      frameWatchdogRef.current = null;
    }

    const video = videoRef.current;
    if (video && frameCallbackRef.current && video.cancelVideoFrameCallback) {
      video.cancelVideoFrameCallback(frameCallbackRef.current);
    }
    frameCallbackRef.current = null;
  };

  const stopVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    video.pause();
    if (video.srcObject) {
      video.srcObject.getTracks().forEach(track => track.stop());
      video.srcObject = null;
    }
  }, []);

  const closeConnection = useCallback(() => {
    const peerConnection = connectionRef.current;
    if (!peerConnection) {
      return;
    }

    peerConnection.onconnectionstatechange = null;
    peerConnection.oniceconnectionstatechange = null;
    peerConnection.onicecandidate = null;
    peerConnection.onnegotiationneeded = null;
    peerConnection.ontrack = null;
    peerConnection.onerror = null;
    peerConnection.close();
    connectionRef.current = null;
  }, []);

  const resetConnection = useCallback(() => {
    clearStallTimer();
    clearFrameWatchdog();
    closeConnection();
    stopVideo();
    setIsPlay(false);
  }, [closeConnection, stopVideo]);

  const scheduleReconnect = useCallback((reason = 'unknown') => {
    if (stoppedRef.current || !streamUrlRef.current) {
      return;
    }

    clearReconnectTimer();
    clearStallTimer();
    clearFrameWatchdog();
    closeConnection();
    stopVideo();
    setIsPlay(false);
    setNoSignal(true);

    const delay = Math.min(
      RECONNECT_BASE_DELAY_MS * (2 ** reconnectAttemptRef.current),
      RECONNECT_MAX_DELAY_MS,
    );
    reconnectAttemptRef.current += 1;
    window.console.warn(`WebRTC reconnect scheduled: ${reason}. Retry in ${delay} ms.`);
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      connectToWebRtc(streamUrlRef.current);
    }, delay);
  }, [closeConnection, stopVideo]);

  const markFrameProgress = useCallback(() => {
    clearStallTimer();
    lastFrameAtRef.current = Date.now();
    reconnectAttemptRef.current = 0;
    setNoSignal(false);
  }, []);

  const startFrameWatchdog = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    clearFrameWatchdog();
    markFrameProgress();

    if (video.requestVideoFrameCallback) {
      const onFrame = () => {
        markFrameProgress();
        if (!stoppedRef.current && videoRef.current === video) {
          frameCallbackRef.current = video.requestVideoFrameCallback(onFrame);
        }
      };
      frameCallbackRef.current = video.requestVideoFrameCallback(onFrame);
    } else if (video.getVideoPlaybackQuality) {
      lastFrameCountRef.current = video.getVideoPlaybackQuality().totalVideoFrames || 0;
    } else {
      return;
    }

    frameWatchdogRef.current = setInterval(() => {
      const currentVideo = videoRef.current;
      if (
        !currentVideo
        || document.hidden
        || userPausedRef.current
        || currentVideo.paused
        || !currentVideo.srcObject
      ) {
        return;
      }

      if (currentVideo.getVideoPlaybackQuality) {
        const frameCount = currentVideo.getVideoPlaybackQuality().totalVideoFrames || 0;
        if (frameCount > lastFrameCountRef.current) {
          lastFrameCountRef.current = frameCount;
          markFrameProgress();
          return;
        }
      }

      if (Date.now() - lastFrameAtRef.current >= VIDEO_STALL_TIMEOUT_MS) {
        scheduleReconnect('video frames stopped');
      }
    }, FRAME_WATCHDOG_INTERVAL_MS);
  }, [markFrameProgress, scheduleReconnect]);

  const armStallReconnect = useCallback(reason => {
    if (userPausedRef.current) {
      return;
    }

    clearStallTimer();
    stallTimerRef.current = setTimeout(() => {
      const video = videoRef.current;
      if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        scheduleReconnect(reason);
      }
    }, VIDEO_STALL_TIMEOUT_MS);
  }, [scheduleReconnect]);

  const handleCameraStream = cameraId => {
    stoppedRef.current = false;
    clearReconnectTimer();
    dispatch(getCameraStream(cameraId))
      .then(url => {
        streamUrlRef.current = resolveMediaServerUrl(url);
        reconnectAttemptRef.current = 0;
        connectToWebRtc(streamUrlRef.current);
      })
      .catch(response => {
        errorNotification(formatMessage({ id: 'camera.get.stream' }), getResponseError(response));
        scheduleReconnect('failed to get stream url');
      });
  };

  useEffect(() => {
    if (id) {
      stoppedRef.current = false;
      userPausedRef.current = false;
      resetConnection();
      handleCameraStream(id);
    } else {
      stoppedRef.current = true;
      streamUrlRef.current = null;
      setNoSignal(false);
      resetConnection();
    }
  }, [id]);

  useEffect(() => () => {
    stoppedRef.current = true;
    clearReconnectTimer();
    resetConnection();
  }, [resetConnection]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return undefined;
    }

    const handleError = () => scheduleReconnect('video element error');
    const handleWaiting = () => armStallReconnect('video waiting too long');
    const handleStalled = () => armStallReconnect('video stalled event');
    const handlePlaying = () => {
      markFrameProgress();
      startFrameWatchdog();
      setIsPlay(true);
    };
    const handlePause = () => setIsPlay(false);

    video.addEventListener('error', handleError);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('stalled', handleStalled);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('error', handleError);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('stalled', handleStalled);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('pause', handlePause);
    };
  }, [armStallReconnect, markFrameProgress, scheduleReconnect, startFrameWatchdog]);

  async function sendSDPToServer(sdp, url) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: new URLSearchParams({ data: btoa(sdp) }),
      });
      const data = await response.text();
      return data.trim();
    } catch (response) {
      errorNotification(formatMessage({ id: 'camera.webrtc.connect' }), getResponseError(response));
      window.console.error(response);
      return null;
    }
  }

  function connectToWebRtc(urlRTC) {
    resetConnection();
    streamUrlRef.current = urlRTC;
    const peerConnection = new RTCPeerConnection(configuration);
    connectionRef.current = peerConnection;
    peerConnection.addEventListener('track', async event => {
      if (connectionRef.current !== peerConnection) {
        return;
      }

      const [remoteStream] = event.streams;
      if (videoRef && videoRef.current) {
        const el = videoRef.current;
        // eslint-disable-next-line prefer-destructuring
        el.srcObject = remoteStream;
        if (!userPausedRef.current) {
          try {
            await el.play();
          } catch (response) {
            window.console.warn(response);
          }
          setIsPlay(true);
          startFrameWatchdog();
        }
      }
    });
    const negotiate = async () => {
      if (connectionRef.current !== peerConnection) {
        return;
      }

      try {
        const offer = await peerConnection.createOffer();
        const iceGathering = waitForIceCandidates(peerConnection);
        await peerConnection.setLocalDescription(offer);
        await iceGathering;
        if (connectionRef.current !== peerConnection || peerConnection.signalingState === 'closed') {
          return;
        }

        const data = await sendSDPToServer(peerConnection.localDescription.sdp, urlRTC);
        if (connectionRef.current !== peerConnection || peerConnection.signalingState === 'closed') {
          return;
        }

        if (data && /^[A-Za-z0-9+/=]+$/.test(data)) {
          const remoteDesc = new RTCSessionDescription({ type: 'answer', sdp: atob(data) });
          await peerConnection.setRemoteDescription(remoteDesc);
          setNoSignal(false);
          armStallReconnect('no video frames after WebRTC answer');
        } else {
          window.console.error(`Received WebRTC answer is not base64: ${data || 'empty response'}`);
          scheduleReconnect('invalid WebRTC answer');
        }
      } catch (response) {
        window.console.error(response);
        if (connectionRef.current === peerConnection) {
          scheduleReconnect('WebRTC negotiation failed');
        }
      }
    };
    peerConnection.onconnectionstatechange = () => {
      if (connectionRef.current !== peerConnection) {
        return;
      }

      switch (peerConnection.connectionState) {
        case 'connected':
          reconnectAttemptRef.current = 0;
          setNoSignal(false);
          armStallReconnect('no video frames after WebRTC connected');
          startFrameWatchdog();
          break;
        case 'failed':
        case 'disconnected':
        case 'closed':
          scheduleReconnect(`WebRTC connection state ${peerConnection.connectionState}`);
          break;
        default:
          break;
      }
    };
    peerConnection.oniceconnectionstatechange = () => {
      if (
        connectionRef.current === peerConnection
        && ['failed', 'disconnected', 'closed'].includes(peerConnection.iceConnectionState)
      ) {
        scheduleReconnect(`ICE connection state ${peerConnection.iceConnectionState}`);
      }
    };
    peerConnection.addTransceiver('video', { direction: 'sendrecv' });
    negotiate();
    peerConnection.onerror = response => {
      window.console.error(response);
      errorNotification(formatMessage({ id: 'camera.webrtc.connect' }), getResponseError(response));
      scheduleReconnect('WebRTC peer error');
    };
  }

  const handleManualReconnect = () => {
    stoppedRef.current = false;
    reconnectAttemptRef.current = 0;
    clearReconnectTimer();
    if (streamUrlRef.current) {
      connectToWebRtc(streamUrlRef.current);
    } else if (id) {
      handleCameraStream(id);
    }
  };

  const handleTogglePlay = () => {
    const el = videoRef.current;
    if (!el) {
      return;
    }

    if (isPlay) {
      userPausedRef.current = true;
      el.pause();
      clearStallTimer();
      clearFrameWatchdog();
      setIsPlay(false);
    } else {
      userPausedRef.current = false;
      el.play()
        .then(() => {
          setIsPlay(true);
          startFrameWatchdog();
        })
        .catch(response => {
          window.console.error(response);
          scheduleReconnect('video play failed');
        });
    }
  };

  if (!selectedCamera) {
    return (
      <div className="no-camera-image">
        <Icons customIcon icon="video" className="no-signal" />
        <div className="no-camera-text">
          {noCameraText || formatMessage({ id: 'session.create.camera-not-selected' })}
        </div>
      </div>
    );
  }

  if (noSignal) {
    return (
      <div className="camera-image-no-signal">
        <Icons customIcon icon="no-signal" className="no-signal" />
        <Icons customIcon icon="reconnect" className="reconnect" onClick={handleManualReconnect} />
      </div>
    );
  }

  return (
    <>
      <div className="camera-control play-stop-button" onClick={handleTogglePlay}>
        <Icons customIcon icon={isPlay ? 'stop' : 'play'} className="play-stop-icon" />
      </div>
      <div className="camera-image"><video ref={videoRef} className="camera-video" id="webrtc-video" autoPlay muted /></div>
    </>
  );
}
